import { useState } from 'react';
import { toast } from 'react-toastify';
import {
    Package, Truck, Clock, Check, Calendar, Hammer, DollarSign,
    TrendingUp, Plus, Edit2, Save, X, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardStats, StatusBadge, ConfirmModal } from '@components/ui';
import clsx from 'clsx';

export default function MaterialTab({ material, onUpdate }) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [isPaymentEditMode, setIsPaymentEditMode] = useState(false);
    const [showPaymentSection, setShowPaymentSection] = useState(true);
    const [showProgressSection, setShowProgressSection] = useState(true);
    const [showAddProgress, setShowAddProgress] = useState(false);
    const [showAddPayment, setShowAddPayment] = useState(false);
    const [editData, setEditData] = useState(null);
    const [paymentEditData, setPaymentEditData] = useState(null);

    // Yeni ilerleme notu
    const [newProgress, setNewProgress] = useState({
        date: new Date().toISOString().split('T')[0],
        note: '',
        percent: material?.iscilik?.progressPercent || 0,
    });

    if (!material) return null;

    const stats = {
        progressPercent: Math.round((material.totalDelivered / material.totalRequired) * 100),
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'delivered':
                return { label: 'Teslim Edildi', variant: 'success' };
            case 'scheduled':
                return { label: 'Planlandı', variant: 'warning' };
            case 'paid':
                return { label: 'Ödendi', variant: 'success' };
            case 'pending':
                return { label: 'Bekliyor', variant: 'warning' };
            default:
                return { label: status, variant: 'default' };
        }
    };

    const getIscilikStatus = (status) => {
        switch (status) {
            case 'completed':
                return { label: 'Tamamlandı', variant: 'success' };
            case 'ongoing':
                return { label: 'Devam Ediyor', variant: 'primary' };
            case 'pending':
                return { label: 'Beklemede', variant: 'warning' };
            default:
                return { label: status, variant: 'default' };
        }
    };

    const handleEditToggle = () => {
        if (!isEditMode) {
            setEditData({
                totalRequired: material.totalRequired,
                totalDelivered: material.totalDelivered,
                contractor: material.iscilik?.contractor || '',
                startDate: material.iscilik?.startDate || '',
                endDate: material.iscilik?.endDate || '',
            });
        }
        setIsEditMode(!isEditMode);
    };

    const handleSave = () => {
        toast.success(`${material.name} bilgileri güncellendi`);
        setIsEditMode(false);
        // onUpdate(editData); // API bağlantısı için
    };

    const handleAddProgress = () => {
        if (!newProgress.note.trim()) {
            toast.error('Lütfen ilerleme notu girin');
            return;
        }
        toast.success('İlerleme notu eklendi');
        setShowAddProgress(false);
        setNewProgress({
            date: new Date().toISOString().split('T')[0],
            note: '',
            percent: newProgress.percent,
        });
        // onUpdate({progressNote: newProgress}); // API bağlantısı için
    };

    // Ödeme düzenleme
    const handlePaymentEditToggle = () => {
        if (!isPaymentEditMode) {
            setPaymentEditData({
                totalAgreed: material.payment?.totalAgreed || 0,
                totalPaid: material.payment?.totalPaid || 0,
            });
        }
        setIsPaymentEditMode(!isPaymentEditMode);
    };

    const handlePaymentSave = () => {
        toast.success(`Ödeme bilgileri güncellendi`);
        setIsPaymentEditMode(false);
    };

    // Yeni ödeme ekleme
    const [newPayment, setNewPayment] = useState({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        note: '',
        status: 'paid',
    });

    const handleAddPayment = () => {
        if (!newPayment.amount) {
            toast.error('Lütfen ödeme tutarı girin');
            return;
        }
        toast.success(`${formatCurrency(parseInt(newPayment.amount))} ödeme eklendi`);
        setShowAddPayment(false);
        setNewPayment({
            date: new Date().toISOString().split('T')[0],
            amount: '',
            note: '',
            status: 'paid',
        });
    };

    return (
        <div className="space-y-4 md:space-y-6">
            {/* İstatistikler */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                <CardStats
                    title="Toplam İhtiyaç"
                    value={`${material.totalRequired.toLocaleString('tr-TR')} ${material.unit}`}
                    icon={Package}
                    color="primary"
                    size="md"
                />
                <CardStats
                    title="Teslim Edilen"
                    value={`${material.totalDelivered.toLocaleString('tr-TR')} ${material.unit}`}
                    icon={Truck}
                    color="success"
                    size="md"
                />
                <CardStats
                    title="Kalan"
                    value={`${(material.totalRequired - material.totalDelivered).toLocaleString('tr-TR')} ${material.unit}`}
                    icon={Clock}
                    color="warning"
                    size="md"
                />
                <CardStats
                    title="İlerleme"
                    value={`%${stats.progressPercent}`}
                    icon={Check}
                    color={stats.progressPercent >= 80 ? 'success' : stats.progressPercent >= 50 ? 'warning' : 'tertiary'}
                    size="md"
                />
            </div>

            {/* İlerleme Çubuğu */}
            <Card padding="lg">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-text-primary">{material.name} İlerlemesi</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">
                            {material.totalDelivered.toLocaleString('tr-TR')} / {material.totalRequired.toLocaleString('tr-TR')} {material.unit}
                        </span>
                        <button
                            onClick={handleEditToggle}
                            className={clsx(
                                "p-2 rounded-lg transition-colors",
                                isEditMode ? "bg-primary text-white" : "hover:bg-bg-tertiary text-text-secondary"
                            )}
                        >
                            {isEditMode ? <X size={16} /> : <Edit2 size={16} />}
                        </button>
                    </div>
                </div>
                <div className="w-full bg-bg-tertiary rounded-full h-4 overflow-hidden">
                    <div
                        className="bg-gradient-to-r from-primary to-success h-full rounded-full transition-all duration-500"
                        style={{ width: `${stats.progressPercent}%` }}
                    />
                </div>

                {/* Düzenleme Modu */}
                {isEditMode && (
                    <div className="mt-4 p-3 sm:p-4 bg-bg-tertiary rounded-lg space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-text-secondary mb-1">Toplam İhtiyaç ({material.unit})</label>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    value={editData?.totalRequired}
                                    onChange={(e) => setEditData({ ...editData, totalRequired: e.target.value })}
                                    className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary text-base"
                                />
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-text-secondary mb-1">Teslim Edilen ({material.unit})</label>
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    value={editData?.totalDelivered}
                                    onChange={(e) => setEditData({ ...editData, totalDelivered: e.target.value })}
                                    className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary text-base"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-text-secondary mb-1">Yüklenici</label>
                                <input
                                    type="text"
                                    value={editData?.contractor}
                                    onChange={(e) => setEditData({ ...editData, contractor: e.target.value })}
                                    className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary text-base"
                                />
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-text-secondary mb-1">Başlangıç</label>
                                <input
                                    type="date"
                                    value={editData?.startDate}
                                    onChange={(e) => setEditData({ ...editData, startDate: e.target.value })}
                                    className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary text-base"
                                />
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-text-secondary mb-1">Bitiş</label>
                                <input
                                    type="date"
                                    value={editData?.endDate}
                                    onChange={(e) => setEditData({ ...editData, endDate: e.target.value })}
                                    className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary text-base"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                            <button
                                onClick={() => setIsEditMode(false)}
                                className="px-4 py-2.5 bg-bg-primary border border-border-light rounded-lg text-text-primary hover:bg-bg-hover min-h-[44px]"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center gap-2 min-h-[44px]"
                            >
                                <Save size={16} />
                                Kaydet
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {/* İşçilik ve İlerleme Durumu */}
            <Card padding="lg">
                <button
                    onClick={() => setShowProgressSection(!showProgressSection)}
                    className="w-full flex items-center justify-between mb-4"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                            <Hammer size={20} className="text-secondary" />
                        </div>
                        <div className="text-left">
                            <h3 className="font-semibold text-text-primary">İşçilik ve İlerleme</h3>
                            <p className="text-sm text-text-muted">{material.iscilik?.contractor || 'Yüklenici belirtilmemiş'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <StatusBadge
                            status={getIscilikStatus(material.iscilik?.status).label}
                            variant={getIscilikStatus(material.iscilik?.status).variant}
                        />
                        {showProgressSection ? <ChevronUp size={20} className="text-text-muted" /> : <ChevronDown size={20} className="text-text-muted" />}
                    </div>
                </button>

                {showProgressSection && (
                    <div className="space-y-4">
                        {/* İşçilik Detayları */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-bg-tertiary rounded-lg">
                            <div>
                                <p className="text-[10px] sm:text-xs text-text-muted">Başlangıç</p>
                                <p className="text-sm sm:text-base font-medium text-text-primary">{formatDate(material.iscilik?.startDate)}</p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs text-text-muted">Bitiş</p>
                                <p className="text-sm sm:text-base font-medium text-text-primary">{formatDate(material.iscilik?.endDate)}</p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs text-text-muted">İlerleme</p>
                                <p className="text-sm sm:text-base font-bold text-primary">%{material.iscilik?.progressPercent || 0}</p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs text-text-muted">Durum</p>
                                <StatusBadge
                                    status={getIscilikStatus(material.iscilik?.status).label}
                                    variant={getIscilikStatus(material.iscilik?.status).variant}
                                />
                            </div>
                        </div>

                        {/* İlerleme Çubuğu */}
                        <div className="relative">
                            <div className="w-full bg-bg-tertiary rounded-full h-3">
                                <div
                                    className="bg-secondary h-full rounded-full transition-all"
                                    style={{ width: `${material.iscilik?.progressPercent || 0}%` }}
                                />
                            </div>
                            <div className="flex justify-between text-xs text-text-muted mt-1">
                                <span>%0</span>
                                <span>%{material.iscilik?.progressPercent || 0}</span>
                                <span>%100</span>
                            </div>
                        </div>

                        {/* İlerleme Notları */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-text-secondary">İlerleme Geçmişi</h4>
                                <button
                                    onClick={() => setShowAddProgress(!showAddProgress)}
                                    className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark"
                                >
                                    <Plus size={16} />
                                    İlerleme Ekle
                                </button>
                            </div>

                            {/* Yeni İlerleme Ekleme */}
                            {showAddProgress && (
                                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs text-text-muted mb-1">Tarih</label>
                                            <input
                                                type="date"
                                                value={newProgress.date}
                                                onChange={(e) => setNewProgress({ ...newProgress, date: e.target.value })}
                                                className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-text-muted mb-1">İlerleme %</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={newProgress.percent}
                                                onChange={(e) => setNewProgress({ ...newProgress, percent: parseInt(e.target.value) || 0 })}
                                                className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={handleAddProgress}
                                                className="w-full px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark"
                                            >
                                                Ekle
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-text-muted mb-1">Not</label>
                                        <input
                                            type="text"
                                            placeholder="Yapılan iş açıklaması..."
                                            value={newProgress.note}
                                            onChange={(e) => setNewProgress({ ...newProgress, note: e.target.value })}
                                            className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Mevcut Notlar */}
                            {material.iscilik?.progressNotes?.length > 0 ? (
                                <div className="space-y-2">
                                    {material.iscilik.progressNotes.map((note, idx) => (
                                        <div key={idx} className="flex items-center gap-3 p-3 bg-bg-secondary rounded-lg">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                <span className="text-xs font-bold text-primary">%{note.percent}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-text-primary truncate">{note.note}</p>
                                                <p className="text-xs text-text-muted">{formatDate(note.date)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-text-muted text-center py-4">Henüz ilerleme notu eklenmemiş</p>
                            )}
                        </div>
                    </div>
                )}
            </Card>

            {/* Ödeme Takibi */}
            {material.payment && (
                <Card padding="lg">
                    <button
                        onClick={() => setShowPaymentSection(!showPaymentSection)}
                        className="w-full flex items-center justify-between mb-4"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                <DollarSign size={20} className="text-success" />
                            </div>
                            <div className="text-left">
                                <h3 className="font-semibold text-text-primary">Ödeme Takibi</h3>
                                <p className="text-sm text-text-muted">
                                    {formatCurrency(material.payment.totalPaid)} / {formatCurrency(material.payment.totalAgreed)}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={clsx(
                                "text-sm font-bold",
                                material.payment.remaining === 0 ? "text-success" : "text-warning"
                            )}>
                                Kalan: {formatCurrency(material.payment.remaining)}
                            </span>
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePaymentEditToggle();
                                }}
                                className={clsx(
                                    "p-1.5 rounded-lg transition-colors cursor-pointer",
                                    isPaymentEditMode ? "bg-primary text-white" : "hover:bg-bg-tertiary text-text-secondary"
                                )}
                            >
                                {isPaymentEditMode ? <X size={16} /> : <Edit2 size={16} />}
                            </div>
                            {showPaymentSection ? <ChevronUp size={20} className="text-text-muted" /> : <ChevronDown size={20} className="text-text-muted" />}
                        </div>
                    </button>

                    {/* Ödeme Düzenleme Modu */}
                    {isPaymentEditMode && (
                        <div className="mb-4 p-4 bg-bg-tertiary rounded-lg space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Anlaşılan Tutar (₺)</label>
                                    <input
                                        type="number"
                                        value={paymentEditData?.totalAgreed}
                                        onChange={(e) => setPaymentEditData({ ...paymentEditData, totalAgreed: e.target.value })}
                                        className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Ödenen Tutar (₺)</label>
                                    <input
                                        type="number"
                                        value={paymentEditData?.totalPaid}
                                        onChange={(e) => setPaymentEditData({ ...paymentEditData, totalPaid: e.target.value })}
                                        className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setIsPaymentEditMode(false)}
                                    className="px-4 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary hover:bg-bg-hover"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handlePaymentSave}
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-2"
                                >
                                    <Save size={16} />
                                    Kaydet
                                </button>
                            </div>
                        </div>
                    )}

                    {showPaymentSection && (
                        <div className="space-y-4">
                            {/* Ödeme Özeti */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-bg-tertiary rounded-lg">
                                <div className="text-center">
                                    <p className="text-[10px] sm:text-xs text-text-muted">Anlaşılan</p>
                                    <p className="text-sm sm:text-base font-bold text-text-primary">{formatCurrency(material.payment.totalAgreed)}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-[10px] sm:text-xs text-text-muted">Ödenen</p>
                                    <p className="text-sm sm:text-base font-bold text-success">{formatCurrency(material.payment.totalPaid)}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-[10px] sm:text-xs text-text-muted">Kalan</p>
                                    <p className="text-sm sm:text-base font-bold text-error">{formatCurrency(material.payment.remaining)}</p>
                                </div>
                            </div>

                            {/* Ödeme İlerleme Çubuğu */}
                            <div className="relative">
                                <div className="w-full bg-bg-tertiary rounded-full h-3">
                                    <div
                                        className="bg-success h-full rounded-full transition-all"
                                        style={{ width: `${(material.payment.totalPaid / material.payment.totalAgreed) * 100}%` }}
                                    />
                                </div>
                                <div className="flex justify-between text-xs text-text-muted mt-1">
                                    <span>%0</span>
                                    <span>%{Math.round((material.payment.totalPaid / material.payment.totalAgreed) * 100)} Ödendi</span>
                                    <span>%100</span>
                                </div>
                            </div>

                            {/* Ödeme Ekle Butonu */}
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-text-secondary">Ödeme Planı</h4>
                                <button
                                    onClick={() => setShowAddPayment(!showAddPayment)}
                                    className="flex items-center gap-1 text-sm text-success hover:text-success/80"
                                >
                                    <Plus size={16} />
                                    Ödeme Ekle
                                </button>
                            </div>

                            {/* Yeni Ödeme Ekleme Formu */}
                            {showAddPayment && (
                                <div className="p-4 bg-success/5 rounded-lg border border-success/20 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                        <div>
                                            <label className="block text-xs text-text-muted mb-1">Tarih</label>
                                            <input
                                                type="date"
                                                value={newPayment.date}
                                                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                                                className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-text-muted mb-1">Tutar (₺)</label>
                                            <input
                                                type="number"
                                                placeholder="0"
                                                value={newPayment.amount}
                                                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                                                className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-text-muted mb-1">Durum</label>
                                            <select
                                                value={newPayment.status}
                                                onChange={(e) => setNewPayment({ ...newPayment, status: e.target.value })}
                                                className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                            >
                                                <option value="paid">Ödendi</option>
                                                <option value="pending">Bekliyor</option>
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={handleAddPayment}
                                                className="w-full px-3 py-2 bg-success text-white rounded-lg text-sm hover:bg-success/90"
                                            >
                                                Ekle
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-text-muted mb-1">Açıklama</label>
                                        <input
                                            type="text"
                                            placeholder="Ödeme açıklaması..."
                                            value={newPayment.note}
                                            onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
                                            className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-text-primary text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Ödeme Planı - Mobil Kart Görünümü */}
                            <div className="block sm:hidden space-y-2">
                                {material.payment.paymentPlan.map((payment) => {
                                    const badge = getStatusBadge(payment.status);
                                    return (
                                        <div key={payment.id} className="p-3 bg-bg-secondary rounded-lg border border-border-light">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-text-primary">{formatDate(payment.date)}</span>
                                                <StatusBadge status={badge.label} variant={badge.variant} />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-text-muted truncate flex-1 mr-2">{payment.note}</span>
                                                <span className={clsx(
                                                    "font-bold text-base",
                                                    payment.status === 'paid' ? "text-success" : "text-text-primary"
                                                )}>
                                                    {formatCurrency(payment.amount)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Ödeme Planı Tablosu - Desktop */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border-light">
                                            <th className="text-left py-2 px-3 text-text-secondary font-medium">Tarih</th>
                                            <th className="text-right py-2 px-3 text-text-secondary font-medium">Tutar</th>
                                            <th className="text-left py-2 px-3 text-text-secondary font-medium">Açıklama</th>
                                            <th className="text-center py-2 px-3 text-text-secondary font-medium">Durum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {material.payment.paymentPlan.map((payment) => {
                                            const badge = getStatusBadge(payment.status);
                                            return (
                                                <tr key={payment.id} className="border-b border-border-light hover:bg-bg-hover">
                                                    <td className="py-2 px-3 text-text-primary">{formatDate(payment.date)}</td>
                                                    <td className={clsx(
                                                        "py-2 px-3 text-right font-medium",
                                                        payment.status === 'paid' ? "text-success" : "text-text-primary"
                                                    )}>
                                                        {formatCurrency(payment.amount)}
                                                    </td>
                                                    <td className="py-2 px-3 text-text-secondary">{payment.note}</td>
                                                    <td className="py-2 px-3 text-center">
                                                        <StatusBadge status={badge.label} variant={badge.variant} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* Teslimat Planı */}
            <Card padding="none" className="overflow-hidden">
                <div className="p-3 sm:p-4 border-b border-border-light">
                    <h3 className="text-sm sm:text-base font-semibold text-text-primary flex items-center gap-2">
                        <Calendar size={18} className="text-primary" />
                        Teslimat Planı ({material.deliveries?.length || 0} parti)
                    </h3>
                </div>

                {/* Mobil - Kart Görünümü */}
                <div className="block sm:hidden p-3 space-y-2">
                    {material.deliveries?.map((delivery) => {
                        const badge = getStatusBadge(delivery.status);
                        return (
                            <div key={delivery.id} className="p-3 bg-bg-secondary rounded-lg border border-border-light">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-text-primary">{formatDate(delivery.date)}</span>
                                    <StatusBadge status={badge.label} variant={badge.variant} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-base font-bold text-primary">
                                        {delivery.quantity.toLocaleString('tr-TR')} {material.unit}
                                    </span>
                                </div>
                                {delivery.notes && (
                                    <p className="text-xs text-text-muted mt-2 truncate">{delivery.notes}</p>
                                )}
                            </div>
                        );
                    })}
                    {(!material.deliveries || material.deliveries.length === 0) && (
                        <div className="text-center py-6 text-text-muted">
                            <Package size={32} className="mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Henüz teslimat bulunmuyor</p>
                        </div>
                    )}
                </div>

                {/* Desktop - Tablo Görünümü */}
                <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-bg-tertiary border-b border-border-light">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Tarih</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-primary">Miktar</th>
                                <th className="text-center py-3 px-4 text-sm font-semibold text-text-primary">Durum</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Notlar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {material.deliveries?.map((delivery, idx) => {
                                const badge = getStatusBadge(delivery.status);
                                return (
                                    <tr
                                        key={delivery.id}
                                        className={clsx(
                                            "border-b border-border-light hover:bg-bg-hover transition-colors",
                                            idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                        )}
                                    >
                                        <td className="py-3 px-4 text-text-primary font-medium">
                                            {formatDate(delivery.date)}
                                        </td>
                                        <td className="py-3 px-4 text-right font-medium text-text-primary">
                                            {delivery.quantity.toLocaleString('tr-TR')} {material.unit}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <StatusBadge status={badge.label} variant={badge.variant} />
                                        </td>
                                        <td className="py-3 px-4 text-text-secondary">
                                            {delivery.notes}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
